<script setup>
import { computed } from "vue";
import AppIcon from "./AppIcon.vue";
import ButtonComponent from "./ButtonComponent.vue";
import ShelfHeader from "./ShelfHeader.vue";
import { ref, reactive } from "vue";
import { findVariableUsages } from "../utils/variables.js";

// `data` is the full story object (storyData.data), not just the variable
// dict, so the usage scanner can see every storylet's cards and loc text.
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

let tempVal = "";
let tempAdd = "";
let filterStr = reactive({
  str: "",
});

function addEnum(variable) {
  if (tempVal.length == 0) return;
  variable.enumValues.push(tempVal);
  tempVal = "";
}

function addVariable() {
  props.data.variableDefs[tempAdd] = {
    type: "int",
    enumValues: [],
    value: 0,
  };
  tempAdd = "";
}

function popEnum(indx, variable) {
  variable.enumValues.splice(indx, 1);
}

function updateFilter() { }

const filteredData = computed(() => {
  return Object.entries(props.data.variableDefs || {})
    .filter(([key, value]) =>
      key.toLowerCase().includes(filterStr["str"].toLowerCase())
    )
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
});

const variableUsages = computed(() => {
  const map = {};
  for (const name of Object.keys(props.data.variableDefs || {})) {
    map[name] = findVariableUsages(props.data, name);
  }
  return map;
});

// Which usage a repeated click on the "jump" button should land on next,
// per variable, so successive clicks cycle through every referencing card.
let usageCursor = reactive({});

function jumpToUsage(name) {
  const hits = variableUsages.value[name];
  if (!hits || hits.length === 0) return;
  const next = ((usageCursor[name] ?? -1) + 1) % hits.length;
  usageCursor[name] = next;
  emit("focus-usage", hits[next]);
}

let deleteConfirm = reactive({});

function deleteVariable(name) {
  if (variableUsages.value[name].length > 0) return;
  if (deleteConfirm[name]) {
    delete props.data.variableDefs[name];
    delete deleteConfirm[name];
  } else {
    deleteConfirm[name] = true;
  }
}

const types = ["string", "int", "float", "enum"];

const emit = defineEmits(["close-win", "focus-usage"]);
</script>

<template>
  <div class="basic-window" v-if="data">
    <div class="ui-container">
      <ShelfHeader title="Variables" @close-win="$emit('close-win')">
        <div class="row">
          <div class="ui-controls">
            <div class="ui-form-textinput">
              <div class="ui-label">Add new</div>
              <input type="text" v-model="tempAdd" v-on:keyup.enter="addVariable()" />
            </div>
          </div>
          <div class="ui-controls">
            <div class="ui-form-textinput">
              <div class="ui-label">Filter</div>
              <input type="text" v-model="filterStr['str']" />
            </div>
          </div>
        </div>
      </ShelfHeader>
      <div class="win-scrollpane">
        <div class="ui-sub-container">
          <div v-bind:key="index" v-for="(variable, index) in filteredData" class="ui-controls vars">
            <div class="row var-header">
              <h4 class="grow">{{ index }}</h4>
              <div class="var-usage" :class="{ 'var-usage-zero': variableUsages[index].length === 0 }">
                {{ variableUsages[index].length === 0 ? "Unused" : `Used in ${variableUsages[index].length} card${variableUsages[index].length === 1 ? "" : "s"}` }}
              </div>
              <ButtonComponent v-if="variableUsages[index].length > 0" icon="arrow-right" color="plain" size="small"
                tooltip="Go to next card using this variable" @click="jumpToUsage(index)" />
              <div v-if="variableUsages[index].length === 0" @mouseleave="deleteConfirm[index] = false">
                <ButtonComponent :icon="deleteConfirm[index] ? 'question' : 'trash'" color="red" size="small"
                  :tooltip="deleteConfirm[index] ? 'Click again to confirm' : 'Delete variable'"
                  @click="deleteVariable(index)" />
              </div>
            </div>
            <div class="bg-vars">
              <div class="row">
                <div class="column-20">
                  <div class="ui-form-textinput">
                    <div class="ui-label">Type</div>
                    <select v-model="variable.type" class="ui-select">
                      <option v-bind:key="ind" v-for="(type, ind) in types" :value="type">
                        {{ type }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="column-20">
                  <div class="ui-form-textinput">
                    <div class="ui-label">Default</div>
                    <div v-if="variable.type != 'enum'">
                      <input v-model="variable.value" />
                    </div>
                    <div v-if="variable.type == 'enum'">
                      <select v-model="variable.value" class="ui-select">
                        <option v-bind:key="index" v-for="(vari, index) in variable.enumValues" :value="vari">
                          {{ vari }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="column-60">
                  <div v-if="variable.type == 'enum'" class="ui-form-textinput">
                    <div class="ui-label">Enum types</div>
                    <div>
                      <div @click="popEnum(indx, variable)" class="enumtag" v-bind:key="indx"
                        v-for="(enumval, indx) in variable.enumValues">
                        {{ enumval }}
                      </div>
                      <input class="inline-input-sm" type="text" v-model="tempVal"
                        v-on:keyup.enter="addEnum(variable)" />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.var-header {
  align-items: center;
  gap: var(--sp-2);
  padding-right: var(--sp-2);
}

.var-header h4 {
  margin: 0;
  padding: 6px 12px;
}

.var-usage {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: var(--text-3);
  white-space: nowrap;
}

.var-usage-zero {
  color: var(--text-dim);
}
</style>
